/**
 * AppointmentSlotPatientList.js
 *
 * Lista compacta de pacientes compatíveis com um slot vago da agenda.
 * Exibida dentro do SlotPopover quando há oportunidades da lista de espera.
 *
 * Layout compacto:
 *   - Ranking numérico (1., 2., ...)
 *   - Nome + procedimento + tempo de espera
 *   - Botões: "Oferecer horario" e "Agendar"
 *   - Selo "Qualquer profissional" (opportunity.anyProfessional): cada entrada
 *     escolhe a agenda entre as livres no horário (patient.freeResourceIds)
 *
 * A oferta pelo WhatsApp fica registrada na entrada da lista de espera, como na
 * tela da lista: set_offer.php (quem e quando) + status "Contatado" (último aviso).
 */

import React, { useState, useCallback } from 'react';
import { isDemoRecord } from '../../../../../components/demo-data/demoData';
import styles from './AppointmentSlotPatientList.module.css';
import { buildWaitingListOfferMessage } from '../../../../../utils/waitingListMessageUtils';
import MessagesTemplate from '../../../../../consts/MessagesTemplate';
import { DataProviderInstance } from '../../../../../api/DataProvider';
import { SendWhatsAppMessage } from '../../../../../api/WhatsappAPI';
import { useCanSendAgendaWhatsApp } from '../../hooks/useAgendaWhatsAppSend';

// ── Ícones SVG inline ─────────────────────────────────────────────────────────

const WhatsappIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const CalendarPlusIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
    <line x1="12" y1="15" x2="12" y2="19"/>
    <line x1="10" y1="17" x2="14" y2="17"/>
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

/**
 * Formata o tempo de espera em texto legível.
 */
function formatWaitingTime(days) {
  if (!days || days <= 0) return null;
  if (days === 1) return '1 dia';
  if (days < 7) return `${days} dias`;
  const weeks = Math.floor(days / 7);
  if (weeks === 1) return '1 sem.';
  if (weeks < 4) return `${weeks} sem.`;
  const months = Math.floor(days / 30);
  if (months === 1) return '1 mes';
  return `${months} meses`;
}

/**
 * Linha compacta de um paciente.
 */
function PatientRow({ patient, rank, slot, onSchedule, onNotifyPatient }) {
  const [scheduling, setScheduling] = useState(false);
  const [offered, setOffered] = useState(false);

  const waitText = formatWaitingTime(patient.waitingDays);

  const { CompanyConfig, SetWaitingListOffer, UpdateWaitingList } = DataProviderInstance();
  const canSendViaSystem = useCanSendAgendaWhatsApp();

  // "Qualquer profissional": a pessoa escolhe a agenda entre as livres no horário
  const agendaChoices = slot.anyProfessional
    ? (patient.freeResourceIds || [])
      .map(id => CompanyConfig?.Resources?.find(r => String(r.Id) === String(id)))
      .filter(Boolean)
    : [];
  const [chosenResourceId, setChosenResourceId] = useState(agendaChoices[0] ? String(agendaChoices[0].Id) : '');
  // Agenda do horário: a escolhida, a da coluna ou a pedida na entrada
  const slotResourceId = slot.anyProfessional
    ? (chosenResourceId || null)
    : (slot.resourceId ?? patient.waitingListItem?.resourceId ?? null);

  // Registra a oferta na entrada (quem ofereceu e quando) e marca "Contatado",
  // que grava a data do último aviso; "Confirmado" não volta atrás
  const recordOffer = useCallback(() => {
    const item = patient.waitingListItem;
    if (!item?.id || isDemoRecord(item)) return;
    SetWaitingListOffer?.({
      waitingListId: item.id,
      patientId: patient.patientId,
      offeredDate: `${slot.date} ${slot.time}:00`,
      channel: 'whatsapp',
    }, () => {});
    if (!item.status || ['PENDING', 'WAITING', 'CONTACTED'].includes(item.status)) {
      UpdateWaitingList?.({ id: item.id, status: 'CONTACTED' }, () => {});
    }
    setOffered(true);
  }, [patient, slot, SetWaitingListOffer, UpdateWaitingList]);

  const handleWhatsapp = useCallback(() => {
    const phone = (patient.patientPhone || '').replace(/\D/g, '');
    if (!phone) return;

    // Pelo sistema (Profissional ou superior / número próprio): a oferta sai na
    // hora pelo backend, com o mesmo template, sem abrir o WhatsApp Web.
    if (canSendViaSystem && onNotifyPatient) {
      onNotifyPatient({
        type: 'slot_offer',
        patient_id: patient.patientId,
        date: slot.date,
        time: slot.time,
        resource_id: slotResourceId,
        procedure_id: patient.waitingListItem?.procedureId || null,
      }, (result) => { if (result?.success) recordOffer(); });
      return;
    }

    const resource = slotResourceId != null
      ? CompanyConfig?.Resources?.find(r => String(r.Id) === String(slotResourceId))
      : null;
    const rawMessage = buildWaitingListOfferMessage({
      patientName: patient.patientName,
      slotDate: slot.date,
      slotTime: slot.time,
      resourceLabel: resource?.Label || (patient.resourceLabel !== 'Qualquer' ? patient.resourceLabel : ''),
      procedureName: patient.procedureLabel,
      companyConfig: CompanyConfig,
    });

    const countryCode = (patient.patientCountry || patient.countryCode || '').replace(/\D/g, '');
    SendWhatsAppMessage(phone, MessagesTemplate.ConvertToWhatsAppText(rawMessage), countryCode);
    recordOffer();
  }, [patient, slot, slotResourceId, CompanyConfig, canSendViaSystem, onNotifyPatient, recordOffer]);

  const handleSchedule = useCallback(async () => {
    if (scheduling) return;
    setScheduling(true);
    try {
      await onSchedule?.(patient, slot.anyProfessional ? { ...slot, resourceId: slotResourceId } : slot);
    } finally {
      setScheduling(false);
    }
  }, [patient, slot, slotResourceId, onSchedule, scheduling]);

  return (
    <div className={styles.patientRow}>
      {/* Linha superior: ranking + info */}
      <div className={styles.patientRowTop}>
        <span className={styles.rank}>{rank}.</span>
        <div className={styles.patientInfo}>
          <span className={styles.patientName}>{patient.patientName || 'Paciente'}</span>
          <span className={styles.patientMeta}>
            {patient.procedureLabel || ''}
            {patient.procedureLabel && waitText ? <span className={styles.sep}>·</span> : null}
            {waitText ? `${waitText}` : null}
            {offered ? <><span className={styles.sep}>·</span>oferta registrada</> : null}
          </span>
          {agendaChoices.length > 0 && (
            <select
              className={styles.agendaSelect}
              value={chosenResourceId}
              onChange={(e) => setChosenResourceId(e.target.value)}
              aria-label="Agenda"
              title="Agenda livre neste horário"
            >
              {agendaChoices.map(r => (
                <option key={r.Id} value={String(r.Id)}>{r.Label}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Linha inferior: ações */}
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.btnOffer}
          onClick={handleWhatsapp}
          title="Oferecer horário via WhatsApp"
          aria-label="Oferecer horário via WhatsApp"
        >
          <WhatsappIcon />
        </button>
        <button
          type="button"
          className={`${styles.btnSchedule} ${scheduling ? styles.btnScheduleLoading : ''}`}
          onClick={handleSchedule}
          disabled={scheduling}
          title="Agendar este paciente neste horário"
          aria-label="Agendar este paciente neste horário"
        >
          <CalendarPlusIcon />
          {scheduling ? '...' : null}
        </button>
      </div>
    </div>
  );
}

/**
 * Lista compacta de pacientes compatíveis com o slot.
 */
export default function AppointmentSlotPatientList({ opportunity, onSchedule, onNotifyPatient }) {
  const [showAll, setShowAll] = useState(false);

  if (!opportunity || !opportunity.patients || opportunity.patients.length === 0) {
    return (
      <div className={styles.section}>
        <div className={styles.emptyState}>Nenhum paciente compativel encontrado.</div>
      </div>
    );
  }

  const { patients, patientCount } = opportunity;
  const MAX_VISIBLE = 3;
  const visiblePatients = showAll ? patients : patients.slice(0, MAX_VISIBLE);
  const hasMore = patientCount > MAX_VISIBLE && !showAll;

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>
          {opportunity.anyProfessional ? 'Lista de espera · qualquer profissional' : 'Lista de espera'}
        </span>
        <span className={styles.sectionBadge}>{patientCount}</span>
      </div>

      <div className={styles.patientList}>
        {visiblePatients.map((patient, idx) => (
          <PatientRow
            key={patient.patientId || idx}
            rank={idx + 1}
            patient={patient}
            slot={opportunity}
            onSchedule={onSchedule}
            onNotifyPatient={onNotifyPatient}
          />
        ))}

        {hasMore && (
          <button
            type="button"
            className={styles.viewMoreBtn}
            onClick={() => setShowAll(true)}
          >
            <ChevronDownIcon />
            Ver mais {patientCount - MAX_VISIBLE}
          </button>
        )}
      </div>
    </div>
  );
}
